/**
 * Service for fetching data from Statistics Norway (SSB) API
 * Documentation: https://www.ssb.no/en/omssb/tjenester-og-verktoy/api
 */

import { Municipality, Fee, YearlyData } from '@/lib/data';
import { getYears, getYearsAsNumbers, getLatestYear } from '@/config/years';

// SSB API endpoints
const SSB_API_URL = 'https://data.ssb.no/api/v0/no/table';

// Table 12842: Kommunale gebyrer knyttet til bolig (K) - år konfigureres i src/config/years.json
const MUNICIPAL_FEES_TABLE = '12842';

// SSB StatBank web URLs
const SSB_STATBANK_URL = 'https://www.ssb.no/statbank/table';

// Utvidet type for intern bruk med totalFees
type MunicipalityWithTotal = Municipality & {
  totalFees: number;
};

// API request payload for getting top/bottom municipalities
const createMunicipalFeesQuery = () => ({
  query: [
    {
      code: "KOKkommuneregion0000",
      selection: {
        filter: "item",
        values: [
          // Lista over større kommuner fra hele landet
          "0301", // Oslo
          "3004", // Fredrikstad
          "3003", // Sarpsborg
          "3005", // Drammen
          "3031", // Nittedal
          "3401", // Kongsvinger
          "3403", // Hamar
          "3407", // Gjøvik
          "3411", // Ringsaker
          "3413", // Stange
          "3420", // Elverum
          "3801", // Horten
          "3803", // Tønsberg
          "3804", // Sandefjord
          "3805", // Larvik
          "3101", // Halden
          "3006", // Kongsberg
          "3007", // Ringerike
          "1103", // Stavanger
          "1108", // Sandnes
          "1106", // Haugesund
          "4204", // Kristiansand
          "4213", // Tvedestrand
          "5001", // Trondheim
          "1201", // Bergen
          "1507", // Ålesund
          "1505"  // Kristiansund
        ]
      }
    },
    {
      code: "ContentsCode",
      selection: {
        filter: "item",
        values: [
          "KOSaarsgebyrvann0000",  // Årsgebyr vann
          "KOSaarsgebyravlo0000",  // Årsgebyr avløp
          "KOSaarsgebyravfa0000",  // Årsgebyr avfall
          "KOSaarsgebyrsept0000",  // Årsgebyr septiktømming
          "KOSfeiingtilsyn0000"    // Årsgebyr for feiing og tilsyn
        ]
      }
    },
    {
      code: "Tid",
      selection: {
        filter: "item",
        values: getYears()
      }
    }
  ],
  response: {
    format: "json-stat2"
  }
});

// API request payload for ALL municipalities
// Bruker SSB filter "all" for å hente alle kommuner - inkludert både gamle (2020-2023)
// og nye (2024+) kommunenummer fra kommunereformen. Data merges i processMunicipalFees.
const createAllMunicipalFeesQuery = () => {
  return {
    query: [
      {
        code: "KOKkommuneregion0000",
        selection: {
          filter: "all",
          values: ["*"]
        }
      },
      {
        code: "ContentsCode",
        selection: {
          filter: "item",
          values: [
            "KOSaarsgebyrvann0000",  // Årsgebyr vann
            "KOSaarsgebyravlo0000",  // Årsgebyr avløp
            "KOSaarsgebyravfa0000",  // Årsgebyr avfall
            "KOSaarsgebyrsept0000",  // Årsgebyr septiktømming
            "KOSfeiingtilsyn0000"    // Årsgebyr for feiing og tilsyn
          ]
        }
      },
      {
        code: "Tid",
        selection: {
          filter: "item",
          values: getYears()
        }
      }
    ],
    response: {
      format: "json-stat2"
    }
  };
};

type SSBResponseValue = {
  value: number[];
  dimension: {
    [key: string]: { 
      category: { 
        index: Record<string, number>, 
        label: Record<string, string> 
      } 
    };
  };
  size: number[];
  status?: Record<string, string>;
};

// Forbedret API-forespørsel for å unngå CORS-problemer på tvers av miljøer
const fetchWithErrorHandling = async (url: string, options: RequestInit): Promise<any> => {
  try {
    const response = await fetch(url, {
      ...options,
      // Sikre at credentials ikke sendes for cross-origin forespørsler
      // Dette kan skape CORS-problemer på produksjon
      credentials: 'omit',
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error fetching data: ${response.status}`, errorText);
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error in fetch operation:', error);
    throw error;
  }
};

/**
 * Fetch municipal fees data from SSB
 */
export const fetchMunicipalFees = async (): Promise<Municipality[]> => {
  try {
    console.log('Fetching municipal fees from SSB...');
    const data = await fetchWithErrorHandling(`${SSB_API_URL}/${MUNICIPAL_FEES_TABLE}`, {
      method: 'POST',
      body: JSON.stringify(createMunicipalFeesQuery())
    });

    return processMunicipalFees(data, true); // Filtrer til topp/bunn 5
  } catch (error) {
    console.error('Error fetching municipal fees:', error);
    throw error;
  }
};

/**
 * Fetch ALL municipal fees data from SSB without filtering
 */
export const fetchAllMunicipalFees = async (): Promise<Municipality[]> => {
  try {
    // Check cache first
    if (window.municipalFeesCache) {
      return window.municipalFeesCache;
    }

    const queryData = createAllMunicipalFeesQuery();
    const url = 'https://data.ssb.no/api/v0/no/table/12842';
    
    const response = await fetchWithErrorHandling(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(queryData)
    });
    
    // Process the data through our converter
    const municipalities = processMunicipalFees(response, false);
    
    // Save to cache
    window.municipalFeesCache = municipalities;
    
    return municipalities;
  } catch (error) {
    console.error('Error fetching all municipal fees from SSB:', error);
    return [];
  }
};

/**
 * Process the JSON-stat2 response from SSB into our application's data model
 */
const processMunicipalFees = (jsonStat: SSBResponseValue, filterTopBottom: boolean = true): Municipality[] => {
  const municipalities: Municipality[] = [];
  let includeCounter = 0;
  let excludeCounter = 0;
  let limitedDataCounter = 0;
  let fullDataCounter = 0;
  
  // Om dette er første gang vi kjører prosesseringen, eller om det er et gjenbruk fra cache
  const isFirstRun = !window.municipalFeesProcessed;

  // Verifiser at responsen inneholder nødvendige data
  if (!jsonStat || !jsonStat.dimension || !jsonStat.value) {
    console.error('Ugyldig respons fra SSB API', jsonStat);
    return [];
  }

  // Hent dimensjonene fra responsen
  const dimensions = jsonStat.dimension;
  const regionKey = "KOKkommuneregion0000"; // Nøkkelen for regioner i API-et
  const Region = dimensions[regionKey];
  
  if (!Region || !Region.category || !Region.category.index || !Region.category.label) {
    console.error('Region dimensjon ikke funnet i responsen eller har uventet format');
    return [];
  }

  if (!dimensions.ContentsCode || !dimensions.ContentsCode.category || 
      !dimensions.ContentsCode.category.index || !dimensions.ContentsCode.category.label) {
    console.error('ContentsCode dimensjon ikke funnet eller har uventet format');
    return [];
  }

  if (!dimensions.Tid || !dimensions.Tid.category || !dimensions.Tid.category.index) {
    console.error('Tid dimensjon ikke funnet eller har uventet format');
    return [];
  }

  const regions = Region.category.index;
  const regionLabels = Region.category.label;
  const feeTypes = dimensions.ContentsCode.category.index;
  const feeLabels = dimensions.ContentsCode.category.label;
  const years = dimensions.Tid.category.index;
  const values = jsonStat.value;
  
  // Sjekk om API-svaret inneholder status for manglende verdier
  const statusCodes = jsonStat.status || {};

  // Finner det nyeste året fra datasetet
  const sortedYears = Object.entries(years).map(([y]) => parseInt(y)).sort((a, b) => b - a);
  const currentYear = sortedYears[0]; // Det nyeste året
  
  // Prosesser hver kommune
  Object.keys(regions).forEach(regionCode => {
    // Hopp over landstotaler og fylkestotaler (starter med EAK, EKA, EKG)
    if (regionCode.startsWith('EAK') || regionCode.startsWith('EKA') || regionCode.startsWith('EKG')) {
      return;
    }

    const id = regionCode; // Bruk koden direkte som ID
    const name = regionLabels[regionCode];
    
    // Hopp over hvis ingen navn (skulle ikke skje)
    if (!name) {
      console.warn(`Manglende navn for region med kode ${regionCode}`);
      return;
    }

    // Kun inkluder gjeldende kommuner – hopp over historiske versjoner med årssuffix
    // SSB har f.eks. "Fredrikstad (2020-2023)", "Tranøy (-2019)" – disse skal ut
    // Men behold "Våler (Østfold)", "Os (Hedmark)" – fylkesdisambiguering
    if (/\(\d{4}|\(\-\d{4}/.test(name)) {
      return;
    }

    const municipalityName = name;
    
    // Bestem hvilket fylke kommunen tilhører basert på kommunekoden
    let county = getCountyFromMunicipalityCode(regionCode);

    // Opprett historiske data for hvert år
    const historicalData: YearlyData[] = [];
    const currentFees: Fee[] = [];
    
    // Flagg for å sjekke om vi har data for denne kommunen
    let hasValidDataForMunicipality = false;
    let hasCurrent2025Data = false;
    let currentYearData: YearlyData | null = null;
    
    // KRITISK ENDRING: Sjekk alltid om vi har minst ett gyldig datapunkt for nyeste år
    const latestYear = getLatestYear();
    let hasLatestYearWaterFee = false;
    let hasLatestYearSewageFee = false;
    let hasLatestYearWasteFee = false;
    let hasLatestYearPropertyTax = false;
    let hasLatestYearChimneySweepFee = false;
    
    // Prosesser hvert år
    Object.entries(years).forEach(([yearStr, yearIndex]) => {
      const year = parseInt(yearStr);
      let waterFee = 0;
      let sewageFee = 0;
      let wasteFee = 0;
      let propertyTax = 0;
      let chimneySweepFee = 0;
      let hasValidData = false;
      let validDataPointsThisYear = 0;

      // Prosesser hver gebyrtype for dette året
      Object.entries(feeTypes).forEach(([feeCode, feeIndex]) => {
        try {
          // Beregn indeksen i verdien-arrayen
          // Formel basert på dimensjonsrekkefølgen i JSON-stat2
          const regionIndex = regions[regionCode];
          const valueIndex = regionIndex * Object.keys(feeTypes).length * Object.keys(years).length 
                           + feeIndex * Object.keys(years).length 
                           + yearIndex;
          
          // Hent verdi eller 0 hvis null/mangler
          if (valueIndex >= values.length) {
            console.warn(`Indeks ${valueIndex} er utenfor grensene for verdier-arrayen (lengde: ${values.length})`);
            return;
          }
          
          const value = values[valueIndex];
          
          // Sjekk om verdien er null eller markert som manglende i status
          const isValueMissing = value === null || (statusCodes[valueIndex] === '.');
          const validValue = isValueMissing ? 0 : (value || 0);

          // Minst én gyldig verdi finnes for denne kommunen/året
          if (!isValueMissing && validValue > 0) {
            hasValidData = true;
            hasValidDataForMunicipality = true;
            validDataPointsThisYear++;
            
            // Sjekk om dette er data for nyeste år
            if (year === latestYear) {
              hasCurrent2025Data = true;
              // For debugging: track hvile typer avgifter vi har for nyeste år
              switch (feeCode) {
                case 'KOSaarsgebyrvann0000':
                  hasLatestYearWaterFee = true;
                  break;
                case 'KOSaarsgebyravlo0000':
                  hasLatestYearSewageFee = true;
                  break;
                case 'KOSaarsgebyravfa0000':
                  hasLatestYearWasteFee = true;
                  break;
                case 'KOSaarsgebyrsept0000':
                  hasLatestYearPropertyTax = true;
                  break;
                case 'KOSfeiingtilsyn0000':
                  hasLatestYearChimneySweepFee = true;
                  break;
              }
            }
          }

          // Tilordne til riktig gebyrtype
          switch (feeCode) {
            case 'KOSaarsgebyrvann0000':
              waterFee = validValue;
              break;
            case 'KOSaarsgebyravlo0000':
              sewageFee = validValue;
              break;
            case 'KOSaarsgebyravfa0000':
              wasteFee = validValue;
              break;
            case 'KOSaarsgebyrsept0000':
              propertyTax = validValue;
              break;
            case 'KOSfeiingtilsyn0000':
              chimneySweepFee = validValue;
              break;
          }
        } catch (error) {
          console.error(`Feil under prosessering av data for ${municipalityName}, år ${year}, gebyrtype ${feeCode}:`, error);
        }
      });

      // FALLBACK: For delvis manglende data – bruk forrige års verdi for avgifter som er 0
      let estimatedWater = false;
      let estimatedSewage = false;
      let estimatedWaste = false;
      let estimatedProperty = false;
      let estimatedChimney = false;
      if (hasValidData && historicalData.length > 0) {
        const prevYearData = [...historicalData].sort((a, b) => b.year - a.year).find(d => d.year < year);
        if (prevYearData) {
          if (waterFee === 0 && prevYearData.waterFee > 0) {
            waterFee = prevYearData.waterFee;
            estimatedWater = true;
          }
          if (sewageFee === 0 && prevYearData.sewageFee > 0) {
            sewageFee = prevYearData.sewageFee;
            estimatedSewage = true;
          }
          if (wasteFee === 0 && prevYearData.wasteFee > 0) {
            wasteFee = prevYearData.wasteFee;
            estimatedWaste = true;
          }
          if (propertyTax === 0 && prevYearData.propertyTax > 0) {
            propertyTax = prevYearData.propertyTax;
            estimatedProperty = true;
          }
          if (chimneySweepFee === 0 && prevYearData.chimneySweepFee > 0) {
            chimneySweepFee = prevYearData.chimneySweepFee;
            estimatedChimney = true;
          }
        }
      }
      const hasFilledFromPrevious = estimatedWater || estimatedSewage || estimatedWaste || estimatedProperty || estimatedChimney;

      const totalFees = waterFee + sewageFee + wasteFee + propertyTax;
      
      // Legg bare til data hvis vi har minst én gyldig verdi
      if (hasValidData) {
        // Opprett årsdata
        const yearData: YearlyData = {
          year,
          waterFee,
          sewageFee,
          wasteFee,
          propertyTax,
          chimneySweepFee,
          totalFees: totalFees + chimneySweepFee, // Inkluderer nå feiegebyr
          ...(hasFilledFromPrevious && { isEstimated: true }),
          ...(estimatedWater && { estimatedWater: true }),
          ...(estimatedSewage && { estimatedSewage: true }),
          ...(estimatedWaste && { estimatedWaste: true }),
          ...(estimatedProperty && { estimatedProperty: true }),
          ...(estimatedChimney && { estimatedChimney: true })
        };
        
        // Legg til i historiske data
        historicalData.push(yearData);

        // Lagre data for nyeste år
        if (year === currentYear) {
          currentYearData = yearData;
          
          // VIKTIG: Sett nåværende gebyrer med isEstimated per kategori
          currentFees.push({ id: 'water', name: 'Vann', amount: waterFee, unit: 'kr/år', isEstimated: estimatedWater });
          currentFees.push({ id: 'sewage', name: 'Avløp', amount: sewageFee, unit: 'kr/år', isEstimated: estimatedSewage });
          currentFees.push({ id: 'waste', name: 'Renovasjon', amount: wasteFee, unit: 'kr/år', isEstimated: estimatedWaste });
          currentFees.push({ id: 'property', name: 'Septiktømming', amount: propertyTax, unit: 'kr/år', isEstimated: estimatedProperty });
          currentFees.push({ id: 'chimney', name: 'Feiing og tilsyn', amount: chimneySweepFee, unit: 'kr/år', isEstimated: estimatedChimney });
        }
      }
    });

    // Sorter historiske data etter år
    historicalData.sort((a, b) => a.year - b.year);
    
    // Sjekk om vi mangler historiske data, men har data for nyeste år
    if (hasValidDataForMunicipality && historicalData.length < 5 && currentYearData) {
      // Finn hvilke år vi mangler data for
      const existingYears = historicalData.map(d => d.year);
      const allExpectedYears = getYearsAsNumbers();
      const missingYears = allExpectedYears.filter(y => !existingYears.includes(y) && y < currentYear);
      
      // Legg til estimerte data for manglende år
      missingYears.forEach(year => {
        if (currentYearData) {
          historicalData.push({
            year,
            waterFee: currentYearData.waterFee,
            sewageFee: currentYearData.sewageFee,
            wasteFee: currentYearData.wasteFee,
            propertyTax: currentYearData.propertyTax,
            chimneySweepFee: currentYearData.chimneySweepFee,
            totalFees: currentYearData.totalFees,
            isEstimated: true // Marker at disse dataene er estimerte
          });
        }
      });
      
      // Sorter historiske data på nytt etter år
      historicalData.sort((a, b) => a.year - b.year);
      
      limitedDataCounter++;
    } else if (historicalData.length >= 5) {
      fullDataCounter++;
    }

    // Spesialbehandling: Hva om vi ikke har noen historiske data i det hele tatt?
    if (historicalData.length === 0 && currentFees.length > 0) {
      // Konstruer grunnleggende data basert på gjeldende avgifter
      const avgWaterFee = currentFees.find(f => f.id === 'water')?.amount || 0;
      const avgSewageFee = currentFees.find(f => f.id === 'sewage')?.amount || 0;
      const avgWasteFee = currentFees.find(f => f.id === 'waste')?.amount || 0;
      const avgPropertyTax = currentFees.find(f => f.id === 'property')?.amount || 0;
      const avgChimneySweepFee = currentFees.find(f => f.id === 'chimney')?.amount || 0;
      const totalFee = avgWaterFee + avgSewageFee + avgWasteFee + avgPropertyTax + avgChimneySweepFee;
      
      // Legg til estimerte data for alle år
      getYearsAsNumbers().forEach(year => {
        historicalData.push({
          year,
          waterFee: avgWaterFee,
          sewageFee: avgSewageFee,
          wasteFee: avgWasteFee,
          propertyTax: avgPropertyTax,
          chimneySweepFee: avgChimneySweepFee,
          totalFees: totalFee,
          isEstimated: true
        });
      });
      
      limitedDataCounter++;
    }
    
    // KRITISK ENDRING: Hvis currentFees er tom, men vi har historiske data, bruk det nyeste året
    if (currentFees.length === 0 && historicalData.length > 0) {
      const latestData = historicalData.sort((a, b) => b.year - a.year)[0];
      
      currentFees.push({ id: 'water', name: 'Vann', amount: latestData.waterFee, unit: 'kr/år', isEstimated: latestData.estimatedWater });
      currentFees.push({ id: 'sewage', name: 'Avløp', amount: latestData.sewageFee, unit: 'kr/år', isEstimated: latestData.estimatedSewage });
      currentFees.push({ id: 'waste', name: 'Renovasjon', amount: latestData.wasteFee, unit: 'kr/år', isEstimated: latestData.estimatedWaste });
      currentFees.push({ id: 'property', name: 'Septiktømming', amount: latestData.propertyTax, unit: 'kr/år', isEstimated: latestData.estimatedProperty });
      currentFees.push({ id: 'chimney', name: 'Feiing og tilsyn', amount: latestData.chimneySweepFee, unit: 'kr/år', isEstimated: latestData.estimatedChimney });
    }

    // NYE INKLUDERINGSKRITERIER: Inkluder alle kommuner som har BÅDE historiske data OG gjeldende avgifter
    if (historicalData.length > 0 && currentFees.length > 0) {
      // Prefer code without year suffix (2024+ structure) som id for bedre SSB-lenker
      const isCurrentCode = !name.includes('(');
      municipalities.push({
        id: isCurrentCode ? regionCode : regionCode, // Brukes ved merge
        _mergeKey: municipalityName,
        _isCurrentCode: isCurrentCode,
        name: municipalityName,
        county,
        population: 0, // SSB gir ikke populasjon i dette datasettet
        currentFees,
        historicalData
      });
      includeCounter++;
    } else {
      excludeCounter++;
    }
  });

  // MERGE: Slå sammen kommuner med samme navn (f.eks. 3004 "Fredrikstad (2020-2023)" og 3107 "Fredrikstad")
  type MergeableMunicipality = Municipality & { _mergeKey?: string; _isCurrentCode?: boolean };
  const mergedByName = new Map<string, Municipality>();
  municipalities.forEach((m: MergeableMunicipality) => {
    const key = m._mergeKey || m.name;
    const existing = mergedByName.get(key);
    const { _mergeKey, _isCurrentCode, ...cleanM } = m;
    if (!existing) {
      mergedByName.set(key, { ...cleanM });
    } else {
      const existingMerge = existing as MergeableMunicipality;
      const newCodes = /^(31|32|33|39|40|55|56)\d{2}$|^1508$|^1580$/;
      const existingIsCurrent = existingMerge.id ? newCodes.test(existingMerge.id) : false;
      const useCurrentAsPrimary = _isCurrentCode && !existingIsCurrent;
      const primary: Municipality = useCurrentAsPrimary ? cleanM : existingMerge;
      const secondary: Municipality = useCurrentAsPrimary ? existingMerge : cleanM;
      const yearMap = new Map<number, YearlyData>();
      [...primary.historicalData, ...secondary.historicalData].forEach(yd => {
        const prev = yearMap.get(yd.year);
        if (!prev || (yd.isEstimated === false && prev.isEstimated === true)) {
          yearMap.set(yd.year, yd);
        }
      });
      const mergedHistorical = Array.from(yearMap.values()).sort((a, b) => a.year - b.year);
      const latestFromMerged = mergedHistorical[mergedHistorical.length - 1];
      mergedByName.set(key, {
        id: primary.id,
        name: primary.name,
        county: primary.county,
        population: 0,
        currentFees: latestFromMerged ? [
          { id: 'water', name: 'Vann', amount: latestFromMerged.waterFee, unit: 'kr/år', isEstimated: latestFromMerged.estimatedWater },
          { id: 'sewage', name: 'Avløp', amount: latestFromMerged.sewageFee, unit: 'kr/år', isEstimated: latestFromMerged.estimatedSewage },
          { id: 'waste', name: 'Renovasjon', amount: latestFromMerged.wasteFee, unit: 'kr/år', isEstimated: latestFromMerged.estimatedWaste },
          { id: 'property', name: 'Septiktømming', amount: latestFromMerged.propertyTax, unit: 'kr/år', isEstimated: latestFromMerged.estimatedProperty },
          { id: 'chimney', name: 'Feiing og tilsyn', amount: latestFromMerged.chimneySweepFee, unit: 'kr/år', isEstimated: latestFromMerged.estimatedChimney }
        ] : primary.currentFees,
        historicalData: mergedHistorical
      });
    }
  });
  const mergedMunicipalities = Array.from(mergedByName.values());

  // ESTIMERING: For kommuner uten data for latestYear (2026), estimer basert på 2025 + gjennomsnittlig økning
  const latestYear = getLatestYear();
  const allExpectedYears = getYearsAsNumbers();
  const yearIndex = allExpectedYears.indexOf(latestYear);
  const prevYear = yearIndex > 0 ? allExpectedYears[yearIndex - 1] : null;

  if (prevYear) {
    const municipalitiesWithPrevYear = mergedMunicipalities.filter(m =>
      m.historicalData.some(d => d.year === prevYear)
    );
    const municipalitiesWithoutLatestYear = mergedMunicipalities.filter(m =>
      !m.historicalData.some(d => d.year === latestYear)
    );

    let avgIncreasePercent = 0;
    let avgIncreaseCount = 0;
    municipalitiesWithPrevYear.forEach(m => {
      const prevData = m.historicalData.find(d => d.year === prevYear);
      const latestData = m.historicalData.find(d => d.year === latestYear);
      if (prevData && latestData && prevData.totalFees > 0 && !prevData.isEstimated) {
        const pct = ((latestData.totalFees - prevData.totalFees) / prevData.totalFees) * 100;
        if (pct > -30 && pct < 50) {
          avgIncreasePercent += pct;
          avgIncreaseCount++;
        }
      }
    });
    avgIncreasePercent = avgIncreaseCount > 0 ? avgIncreasePercent / avgIncreaseCount : 3; // Default 3% om ingen data

    municipalitiesWithoutLatestYear.forEach(m => {
      const prevData = m.historicalData.find(d => d.year === prevYear);
      if (prevData && prevData.totalFees > 0) {
        const factor = 1 + avgIncreasePercent / 100;
        const estimated: YearlyData = {
          year: latestYear,
          waterFee: Math.round(prevData.waterFee * factor),
          sewageFee: Math.round(prevData.sewageFee * factor),
          wasteFee: Math.round(prevData.wasteFee * factor),
          propertyTax: Math.round(prevData.propertyTax * factor),
          chimneySweepFee: Math.round(prevData.chimneySweepFee * factor),
          totalFees: 0,
          isEstimated: true
        };
        estimated.totalFees = estimated.waterFee + estimated.sewageFee + estimated.wasteFee + estimated.propertyTax + estimated.chimneySweepFee;
        m.historicalData.push(estimated);
        m.historicalData.sort((a, b) => a.year - b.year);
        m.currentFees = [
          { id: 'water', name: 'Vann', amount: estimated.waterFee, unit: 'kr/år', isEstimated: true },
          { id: 'sewage', name: 'Avløp', amount: estimated.sewageFee, unit: 'kr/år', isEstimated: true },
          { id: 'waste', name: 'Renovasjon', amount: estimated.wasteFee, unit: 'kr/år', isEstimated: true },
          { id: 'property', name: 'Septiktømming', amount: estimated.propertyTax, unit: 'kr/år', isEstimated: true },
          { id: 'chimney', name: 'Feiing og tilsyn', amount: estimated.chimneySweepFee, unit: 'kr/år', isEstimated: true }
        ];
        limitedDataCounter++;
      }
    });
  }

  // Bruk merged liste med estimering
  let processedMunicipalities = mergedMunicipalities;

  // Bare vis statistikk hvis dette er første gang vi prosesserer dataene
  if (isFirstRun) {
    console.log("\n----------------------------------------");
    console.log(`Statistikk for prosessering:`);
    console.log(`- Rå poster fra SSB (før sammenslåing): ${includeCounter}`);
    console.log(`- Unike kommuner etter sammenslåing: ${mergedMunicipalities.length}`);
    console.log(`- Ekskludert (mangler data): ${excludeCounter}`);
    console.log(`- Kommuner med fullstendige data: ${fullDataCounter}`);
    console.log(`- Kommuner med estimerte historiske data: ${limitedDataCounter}`);
    
    // Marker at vi har kjørt prosesseringen én gang
    window.municipalFeesProcessed = true;
  }

  // Filtrer eller behold alle avhengig av filterTopBottom-parameteren
  // (processedMunicipalities er allerede satt fra mergedMunicipalities over)
  
  // Velg ut topp/bunn kommuner hvis vi skal filtrere
  if (filterTopBottom) {
    // Sorter etter totale avgifter
    const sortedMunicipalities = [...processedMunicipalities].sort(
      (a, b) => {
        const totalA = a.currentFees.reduce((sum, fee) => sum + fee.amount, 0);
        const totalB = b.currentFees.reduce((sum, fee) => sum + fee.amount, 0);
        return totalB - totalA; // Høyest først
      }
    );
    
    // Hent top 5 og bottom 5
    const highestFees = sortedMunicipalities.slice(0, 5);
    const lowestFees = sortedMunicipalities.slice(-5).reverse();
    
    // Kombiner med noen store kommuner for representativt utvalg
    const largestCities = processedMunicipalities.filter(m => 
      ["Oslo", "Bergen", "Trondheim", "Stavanger", "Kristiansand", 
       "Fredrikstad", "Sandnes", "Drammen", "Asker", "Bærum", "Lillestrøm"]
        .includes(m.name)
    );
    
    // Legg sammen alle lister og fjern duplikater
    processedMunicipalities = [
      ...highestFees, 
      ...largestCities, 
      ...lowestFees
    ].filter((municipality, index, self) => 
      index === self.findIndex(m => m.id === municipality.id)
    );
  }
  
  return processedMunicipalities;
};

/**
 * Hjelpefunksjon som bestemmer fylket basert på kommunekoden
 * Kommunenummer er tildelt fylkesvis - første siffer(e) indikerer fylket
 */
function getCountyFromMunicipalityCode(code: string): string {
  // Fjern alle ikke-numeriske tegn og konverter til tall
  const cleanCode = code.replace(/\D/g, '');
  const codeNum = parseInt(cleanCode, 10);
  
  // Basert på kommunenummer-systemet i Norge - 2024+ fylkesinndeling først
  if (codeNum === 301) return 'Oslo';
  if (codeNum >= 3100 && codeNum < 3200) return 'Østfold';
  if (codeNum >= 3200 && codeNum < 3300) return 'Akershus';
  if (codeNum >= 3300 && codeNum < 3400) return 'Buskerud';
  if (codeNum >= 3900 && codeNum < 4000) return 'Vestfold';
  if (codeNum >= 4000 && codeNum < 4100) return 'Telemark';
  if (codeNum >= 5500 && codeNum < 5600) return 'Troms';
  if (codeNum >= 5600 && codeNum < 5700) return 'Finnmark';
  if (codeNum >= 1100 && codeNum < 1200) return 'Rogaland';
  if (codeNum >= 1500 && codeNum < 1600) return 'Møre og Romsdal';
  if (codeNum >= 1800 && codeNum < 1900) return 'Nordland';
  if (codeNum >= 3400 && codeNum < 3500) return 'Innlandet';
  if (codeNum >= 4200 && codeNum < 4300) return 'Agder';
  if (codeNum >= 5000 && codeNum < 5100) return 'Trøndelag';
  if (codeNum >= 1200 && codeNum < 1300) return 'Vestland';
  if (codeNum >= 4600 && codeNum < 4700) return 'Vestland';
  // Gamle koder (før 2024) for kommuner som ikke ble delt
  if (codeNum >= 3800 && codeNum < 3900) return 'Vestfold';
  if (codeNum >= 5400 && codeNum < 5500) return 'Troms og Finnmark';
  if (codeNum >= 3000 && codeNum < 3100 && codeNum !== 301) return 'Viken'; // Gamle Viken-koder (2020-2023)
  
  // Tilleggssjekker for andre fylker som mangler fra den opprinnelige listen
  if (codeNum >= 1000 && codeNum < 1100) return 'Agder'; // Eldre Agder-koder
  if (codeNum >= 4000 && codeNum < 4200) return 'Agder'; // Nyere Agder-koder
  
  // KOSTRA-grupperinger og andre SSB-koder (08xx m.m.) – vis som "Annet fylke" uten logging
  if (codeNum > 0 && codeNum < 6000) return 'Annet fylke';
  return 'Ukjent';
}

/**
 * Fetch population data for municipalities (optional enhancement)
 * This would require a separate API call to SSB table 07459
 */
export const fetchPopulationData = async (): Promise<Record<string, number>> => {
  // Implementation for a future enhancement
  return {};
};

/**
 * Genererer en direktelenke til SSB StatBank for en spesifikk kommune
 * Dette gjør det mulig for brukere å sjekke de originale dataene direkte fra SSB
 */
export const generateSSBLinkForMunicipality = (municipalityId: string): string => {
  // Basislenke til SSB StatBank for tabellen
  const baseUrl = `${SSB_STATBANK_URL}/${MUNICIPAL_FEES_TABLE}`;
  
  // Vi legger til parametere for å pre-velge denne kommunen i StatBank-grensesnittet
  // Med de viktigste gebyrene
  return `${baseUrl}?KOKkommuneregion0000=${municipalityId}&ContentsCode=KOSaarsgebyrvann0000&ContentsCode=KOSaarsgebyravlo0000&ContentsCode=KOSaarsgebyravfa0000&ContentsCode=KOSaarsgebyrsept0000&ContentsCode=KOSfeiingtilsyn0000`;
};

/**
 * Når vi finner riktig ContentCode for feiegebyr, kan vi erstatte den estimerte verdien med
 * faktiske data fra SSB API. Dette vil kreve at vi inkluderer den riktige koden i
 * ContentsCode-listene i API-forespørslene.
 * 
 * Eksempel:
 * "KOSaarsgebyrfeie0000" - Dette er ikke en verifisert kode ennå.
 */

// Add this to global Window interface at the top of the file
declare global {
  interface Window {
    municipalFeesCache?: Municipality[];
    municipalFeesProcessed?: boolean;
  }
}
